import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { Link, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Grid,
  gridClasses,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BalconyOutlinedIcon from "@mui/icons-material/BalconyOutlined";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import { DataGrid } from "@mui/x-data-grid";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useOrganization } from "../utils/OrganizationContext";
import ThemedSelect from "../utils/ThemedSelect";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const lbl = { display: "block", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: "#3D4C60" };
const smallBtn = {
  height: 30,
  minWidth: 0,
  px: 1.5,
  borderRadius: "8px",
  border: `1px solid ${FIELD_EDGE}`,
  background: "#FFFFFF",
  color: INK,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#EAF2FB", borderColor: "#C9DEF3", color: "#1B4F82" },
};
// Status pill tint, keyed on the status text (display only)
const statusTone = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("avail")) return { bg: "#E9F7EE", edge: "#C4E8D2", ink: "#1B6B41" };
  if (v.includes("sold")) return { bg: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A" };
  if (v.includes("rent") || v.includes("hold") || v.includes("book")) return { bg: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08" };
  return { bg: "#F1F4F8", edge: LINE, ink: "#4A5A6E" };
};
const metaLabel = { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: MUTED };
const metaValue = { fontFamily: MONO, fontSize: 13.5, fontWeight: 600, color: INK };

const INVENTORY_TABLE_COLUMNS = [
  { key: "type", label: "Type" },
  { key: "size", label: "Size" },
  { key: "status", label: "Status" },
  { key: "floor", label: "Floor" },
  { key: "totalFloor", label: "Total Floors" },
  { key: "parking", label: "Parking" },
  { key: "balcony", label: "Balcony" },
  { key: "bedroom", label: "Bedrooms" },
  { key: "bath", label: "Bathrooms" },
  {
    key: "possessionDate",
    label: "Possession Date",
    format: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
  { key: "price", label: "Price" },
  { key: "rent", label: "Rent" },
  { key: "age", label: "Age" },
  { key: "address", label: "Address" },
  { key: "ownerName", label: "Owner Name" },
  {
    key: "ownerContact",
    label: "Owner Contact",
    format: (value) => (value ? `+${value}` : "-"),
  },
  { key: "media", label: "Media" },
];

const InventoryList = () => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  // const [role, setRole] = useState();
  const [projects, setProjects] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    JSON.parse(localStorage.getItem("projectId")) || "",
  );
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [filters, setFilters] = useState({ search: "", type: "", size: "", status: "" });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState([]);
  const [size, setSize] = useState([]);
  const [openMediaModal, setOpenMediaModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [activeMediaStep, setActiveMediaStep] = useState(0);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { role } = useOrganization();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 🔥 ADDED: debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    if (!storedUser?.accessToken) return;

    // const decoded = jwtDecode(storedUser.accessToken);
    // const role = decoded.userRole?.[0];
    // setRole(role);
    fetchProjects(role);
  }, [orgId]);

  const fetchProjects = async (role) => {
    try {
      const url =
        role === "ADMIN"
          ? `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`
          : `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });

      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to fetch projects", err);
    }
  };

  useEffect(() => {
    fetchInventories(selectedProject);
  }, [selectedProject]);

  const fetchInventories = async (projectId) => {
    if (!projectId) {
      setInventories([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inventory/project?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setInventories(res.data || []);
      const getUniqueValues = (arr, key) => {
        return [
          ...new Set(
            arr
              ?.map((item) => item[key])
              .filter((val) => val !== null && val !== undefined && val !== ""),
          ),
        ];
      };
      setTypes(getUniqueValues(res.data, "type"));
      setStatus(getUniqueValues(res.data, "status"));
      setSize(getUniqueValues(res.data, "size"));
    } catch (err) {
      console.error("Failed to fetch inventories", err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);
    fetchInventories(projectId);
    localStorage.setItem("projectId", JSON.stringify(e.target.value));
  };

  const filteredInventories = inventories.filter((inv) => {
    const search = debouncedSearch.toLowerCase();

    const matchesSearch = Object.values(inv).some(
      (val) =>
        val !== null &&
        val !== undefined &&
        String(val).toLowerCase().includes(search),
    );

    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value || key === "search") return true;

      const fieldValue = inv[key];
      if (fieldValue === null || fieldValue === undefined) return false;

      if (value === "Others" && key === "type") {
        return !["2BHK", "3BHK", "4BHK"].includes(String(fieldValue));
      }

      return String(fieldValue)
        .toLowerCase()
        .includes(String(value).toLowerCase());
    });

    return matchesSearch && matchesFilters;
  });

  const columns = [
    ...INVENTORY_TABLE_COLUMNS.map((col) => ({
      field: col.key,
      headerName: col.label,
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.row[col.key];

        if (col.key === "ownerContact") {
          return (
            <span style={{ fontFamily: MONO, fontSize: 12.5 }}>
              {value
                ? params.row.countryCode !== null
                  ? params.row.countryCode.includes("+")
                    ? `${params.row.countryCode}${value}`
                    : `+${params.row.countryCode}${value}`
                  : col.format(value)
                : "-"}
            </span>
          );
        }

        if (col.key === "media") {
          console.log(value);
          return value?.length > 0 ? (
            <Button
              size="small"
              onClick={() => {
                setSelectedMedia(value);
                setOpenMediaModal(true);
              }}
              sx={smallBtn}
            >
              View
            </Button>
          ) : (
            "-"
          );
        }

        if (col.key === "status" && value) {
          const t = statusTone(value);
          return (
            <span style={{ padding: "2px 9px", borderRadius: 999, background: t.bg, border: `1px solid ${t.edge}`, color: t.ink, fontSize: 12, fontWeight: 600 }}>
              {value}
            </span>
          );
        }

        if (col.key === "type") {
          return <span style={{ fontWeight: 700 }}>{value ?? "-"}</span>;
        }

        if (col.format) {
          return col.format(value);
        }

        return value ?? "-";
      },
    })),

    // Action column
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() =>
            navigate(
              role === "ADMIN"
                ? `/dashboard/inventory/edit-inventory/${params.row.inventoryId}`
                : `/dashboard/inventory/edit-inventory/${params.row.inventoryId}`,
            )
          }
          sx={smallBtn}
        >
          Edit
        </Button>
      ),
    },
  ];

  const numericCols = ["size", "floor", "totalFloor", "parking", "balcony", "bedroom", "bath", "price", "rent", "age", "possessionDate"];

  return (
    <div style={{ paddingY: 20, fontFamily: FONT, color: INK, textAlign: "left" }}>
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 5,
          mb: 2,
        }}
      >
        <Typography
          variant="h4"
          my={1}
          sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}
        >
          Inventory List
        </Typography>
        <Box sx={{ display: { xs: "block", sm: "flex" }, gap: 3 }}>
          <Link
            to={
              role === "ADMIN"
                ? `/dashboard/inventory/add-inventory`
                : "/dashboard/inventory/add-inventory"
            }
            style={{ textDecoration: "none" }}
          >
            <Button
              className="btn-contained"
              sx={{
                width: { xs: "100%", sm: "auto" },
                mb: { xs: 1, sm: 0 },
                height: 40,
                px: 2,
                margin: "0 !important",
                gap: 1,
                borderRadius: "10px !important",
                background: `${PRIMARY} !important`,
                color: "#FFFFFF !important",
                border: "0 !important",
                fontFamily: FONT,
                fontSize: 13.5,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: "#084767 !important" },
              }}
            >
              <PersonAddAltRoundedIcon sx={{ fontSize: 18 }} /> Add Inventory
            </Button>
          </Link>
        </Box>
      </Box>

      {/* Project Selector + filters */}
      <Box sx={{ p: "14px 16px 4px", mb: 2, background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "14px" }}>
        <label style={lbl}>Select Project</label>
        <Autocomplete
          name="projectId"
          style={{ width: "100%", maxWidth: 400, marginBottom: 14 }}
          options={projects || []}
          getOptionLabel={(option) => option.projectName || ""}
          value={projects?.find((p) => p.projectId === selectedProject) || null}
          onChange={(e, value) => {
            setSelectedProject(value.projectId);
            fetchInventories(value.projectId);
            localStorage.setItem("projectId", JSON.stringify(value.projectId));
          }}
          isOptionEqualToValue={(option, value) =>
            option.projectId === value.projectId
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Project" />
          )}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                borderRadius: "12px",
                border: `1px solid ${LINE}`,
                boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
                "& .MuiAutocomplete-option": { fontFamily: FONT, fontSize: 13.5, borderRadius: "8px", mx: 0.75 },
              },
            },
          }}
          sx={{
            margin: "0 0 14px 0",
            "& .MuiInputBase-root": {
              minHeight: 42,
              fontFamily: FONT,
              fontSize: "14px",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "1px 7px",
            },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${PRIMARY} !important`, borderWidth: "1px !important" },
          }}
        />

        <Grid container spacing={1.5} sx={{ pb: 1.5, pt: 1.5, borderTop: "1px solid #EDF1F5" }}>
          <Grid
            size={{ xs: 12, sm: 2.4 }}
            sx={{ display: "flex", alignItems: "end" }}
          >
            <input
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              style={{ display: "block", width: "100%", boxSizing: "border-box", height: 42, padding: "0 12px", margin: 0, background: "#FFFFFF", border: `1px solid ${FIELD_EDGE}`, borderRadius: 10, fontFamily: FONT, fontSize: 14, color: INK, outline: "none" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2.4 }}>
            <label htmlFor="type" style={lbl}>Filter By Type</label>
            <ThemedSelect
              name="type"
              id="type"
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              placeholder="--Choose--"
              options={[{ value: "", label: "--Choose--" }, ...types.map((type) => ({ value: type, label: type }))]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2.4 }}>
            <label htmlFor="size" style={lbl}>Filter By Size</label>
            <ThemedSelect
              name="size"
              id="size"
              value={filters.size}
              onChange={(e) => handleFilterChange("size", e.target.value)}
              placeholder="--Choose--"
              options={[{ value: "", label: "--Choose--" }, ...size.map((size) => ({ value: size, label: String(size) }))]}
            />
          </Grid>
          <Grid size={{ xs: 8, sm: 2.4 }}>
            <label htmlFor="status" style={lbl}>Filter By Status</label>
            <ThemedSelect
              name="status"
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              placeholder="--Choose--"
              options={[{ value: "", label: "--Choose--" }, ...status.map((status) => ({ value: status, label: status }))]}
            />
          </Grid>
          <Grid
            size={{ xs: 4, sm: 2.4 }}
            sx={{ display: "flex", alignItems: "end" }}
          >
            <Button
              onClick={() =>
                setFilters({ search: "", type: "", size: "", status: "" })
              }
              sx={{ ...smallBtn, height: 42, width: "100%", borderRadius: "10px" }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Inventory Table */}
      {loading ? (
        <p style={{ margin: "28px 0", textAlign: "center", fontSize: 13.5, color: MUTED }}>Loading inventories...</p>
      ) : filteredInventories.length === 0 ? (
        <p style={{ margin: 0, padding: "28px 16px", textAlign: "center", fontSize: 13.5, color: MUTED, background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14 }}>No inventories found</p>
      ) : (
        <>
          {isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredInventories?.map((inv) => {
                const t = statusTone(inv.status);
                return (
                <div
                  key={inv.inventoryId}
                  className="card"
                  style={{ margin: 0, padding: "14px 16px", background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, boxShadow: "none" }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{ display: "flex", alignItems: "baseline", gap: 10 }}
                      >
                        <Typography
                          fontSize={"18px"}
                          color={INK}
                          fontWeight={"800"}
                          sx={{ fontFamily: FONT, letterSpacing: "-0.02em" }}
                        >
                          {inv.type}
                        </Typography>
                        <Typography
                          fontSize={"12.5px"}
                          color={MUTED}
                          fontWeight={"500"}
                          sx={{ fontFamily: MONO }}
                        >
                          {inv.size} sq.ft.
                        </Typography>
                      </div>
                      <Typography
                        fontSize={"13px"}
                        color={MUTED}
                        fontWeight={"500"}
                        sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, fontFamily: FONT }}
                      >
                        <LocationOnOutlinedIcon sx={{ fontSize: 15, color: "#8A97A8" }} />{" "}
                        {inv.address}
                      </Typography>
                    </div>
                    <div
                      className="card-tag"
                      style={{ flex: "0 0 auto", alignSelf: "flex-start", margin: 0, padding: "2px 9px", borderRadius: 999, background: t.bg, border: `1px solid ${t.edge}`, color: t.ink, fontSize: 12, fontWeight: 600 }}
                    >
                      {inv.status}
                    </div>
                  </div>
                  <Grid container mt={1.5} spacing={1.25}>
                    <Grid size={{ xs: 6 }}>
                      <div style={{ padding: "8px 10px", background: "#F7F9FC", borderRadius: 10 }}>
                        <Typography sx={metaLabel}>
                          FLOOR
                        </Typography>
                        <Typography sx={metaValue}>
                          {inv.floor} / {inv.totalFloor}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <div style={{ padding: "8px 10px", background: "#F7F9FC", borderRadius: 10 }}>
                        <Typography sx={metaLabel}>
                          PRICE
                        </Typography>
                        <Typography sx={metaValue}>
                          {new Intl.NumberFormat("en-IN", {
                            style: "currency",
                            currency: "INR",
                            maximumFractionDigits: 2,
                          }).format(inv.price)}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 10px",
                        }}
                      >
                        <Typography
                          color={MUTED}
                          fontSize={"13px"}
                          fontWeight={600}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 16 }} />
                          <span style={{ color: INK, fontFamily: MONO }}>
                            {inv.parking}
                          </span>
                        </Typography>
                        <Typography
                          color={MUTED}
                          fontSize={"13px"}
                          fontWeight={600}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <BalconyOutlinedIcon sx={{ fontSize: 16 }} />
                          <span style={{ color: INK, fontFamily: MONO }}>
                            {inv.balcony}
                          </span>
                        </Typography>
                        <Typography
                          color={MUTED}
                          fontSize={"13px"}
                          fontWeight={600}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <BedOutlinedIcon sx={{ fontSize: 16 }} />{" "}
                          <span style={{ color: INK, fontFamily: MONO }}>
                            {inv.bedroom}
                          </span>
                        </Typography>
                      </div>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <div style={{ padding: "8px 10px", background: "#F7F9FC", borderRadius: 10 }}>
                        <Typography sx={metaLabel}>
                          POSSESSION
                        </Typography>
                        <Typography sx={metaValue}>
                          {new Date(inv.possessionDate).toLocaleDateString()}
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>

                  <hr style={{ border: 0, borderTop: "1px solid #EDF1F5", margin: "12px 0" }} />
                  <div
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Typography
                      color={MUTED}
                      fontSize={"13px"}
                      fontWeight={600}
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <MeetingRoomOutlinedIcon sx={{ fontSize: 16 }} />
                      <span style={{ color: INK }}>
                        {inv.age} Years Old
                      </span>
                    </Typography>
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={() =>
                        navigate(
                          role === "ADMIN"
                            ? `/dashboard/inventory/edit-inventory/${inv.inventoryId}`
                            : `/dashboard/inventory/edit-inventory/${inv.inventoryId}`,
                        )
                      }
                      sx={{ ...smallBtn, height: 40, px: 2.5, color: `${INK} !important` }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
                );
              })}
            </Box>
          ) : (
            // <div
            //   style={{
            //     width: "100%",
            //     overflowX: "auto",
            //   }}
            // >
            //   <table
            //     border="1"
            //     cellPadding="8"
            //     style={{ minWidth: "100%", borderCollapse: "collapse" }}
            //   >
            //     <thead>
            //       <tr>
            //         {INVENTORY_TABLE_COLUMNS.map((col) => (
            //           <th key={col.key}>{col.label}</th>
            //         ))}
            //         <th>Action</th>
            //       </tr>
            //     </thead>

            //     <tbody>
            //       {filteredInventories.map((inv) => (
            //         <tr key={inv.inventoryId}>
            //           {INVENTORY_TABLE_COLUMNS.map((col) => (
            //             <td key={col.key}>
            //               {col.format
            //                 ? col.format(inv[col.key])
            //                 : (inv[col.key] ?? "-")}
            //             </td>
            //           ))}

            //           <td>
            //             <Button
            //               size="small"
            //               onClick={() =>
            //                 navigate(
            //                   role === "ADMIN"
            //                     ? `/dashboard/inventory/edit-inventory/${inv.inventoryId}`
            //                     : `/dashboard/inventory/edit-inventory/${inv.inventoryId}`,
            //                 )
            //               }
            //             >
            //               Edit
            //             </Button>
            //           </td>
            //         </tr>
            //       ))}
            //     </tbody>
            //   </table>
            // </div>
            <Box sx={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden" }}>
              <DataGrid
                rows={filteredInventories}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 20,
                    },
                  },
                }}
                pageSizeOptions={[20]}
                disableRowSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                // slots={{ toolbar: CustomToolbar }}
                getRowId={(row) => row.inventoryId}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
                getCellClassName={(params) => (numericCols.includes(params.field) ? "num" : "")}
                rowHeight={50}
                sx={{
                  position: "relative",
                  border: "none",
                  fontFamily: `${FONT} !important`,
                  color: INK,

                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "700 !important",
                    fontSize: 10.5,
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    color: MUTED,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    background: "#F7F9FC !important",
                    color: MUTED,
                    fontSize: "14px",
                    borderRadius: 0,
                  },
                  "& .MuiDataGrid-columnHeader": { background: "#F7F9FC" },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#F7F9FC",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: "13.5px",
                    border: "none",
                    borderBottom: "1px solid #F1F4F8",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-cell.num": { fontFamily: MONO, fontSize: "12.5px" },
                  "& .MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
                  "& .MuiInputBase-root-MuiInput-root::after": {
                    borderBottom: "none !important",
                  },
                  "& .MuiInputBase-root-MuiInput-root": {
                    borderBottom: "none !important",
                  },
                  [`& .${gridClasses.row}.even`]: {
                    backgroundColor: "#FFFFFF",
                    // '&:hover': {
                    //   backgroundColor: "#f6f6f6",
                    // },
                  },
                  "& .MuiInput-underline:before": {
                    borderBottom: "none !important",
                  },
                  "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${LINE}` },
                  "& .MuiTablePagination-displayedRows": { fontFamily: MONO, fontSize: 12, color: MUTED },
                }}
              />
            </Box>
          )}
        </>
      )}

      <Dialog
        open={openMediaModal}
        onClose={() => {
          setOpenMediaModal(false);
          setActiveMediaStep(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" } }}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <DialogTitle sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
          Inventory Media
        </DialogTitle>

        <DialogContent>
          {selectedMedia?.length > 0 && (
            <>
              {(() => {
                const media = selectedMedia[activeMediaStep];

                const isVideo =
                  media.url?.includes(".mp4") ||
                  media.url?.includes(".mov") ||
                  media.url?.includes(".webm");

                return (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 300,
                      background: "#F7F9FC",
                      border: `1px solid ${LINE}`,
                      borderRadius: 14,
                    }}
                  >
                    {isVideo ? (
                      <video
                        src={media.url}
                        controls
                        style={{
                          width: "100%",
                          maxHeight: 300,
                          borderRadius: 12,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <img
                        src={media.url}
                        alt={`media-${activeMediaStep}`}
                        style={{
                          width: "100%",
                          maxHeight: 300,
                          objectFit: "contain",
                          borderRadius: 12,
                        }}
                      />
                    )}
                  </div>
                );
              })()}

              <MobileStepper
                steps={selectedMedia.length}
                position="static"
                activeStep={activeMediaStep}
                sx={{
                  background: "transparent",
                  px: 0,
                  "& .MuiMobileStepper-dotActive": { background: PRIMARY },
                }}
                nextButton={
                  <IconButton
                    onClick={() => setActiveMediaStep((prev) => prev + 1)}
                    disabled={activeMediaStep === selectedMedia.length - 1}
                    sx={{ border: `1px solid ${LINE}`, borderRadius: "9px" }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                }
                backButton={
                  <IconButton
                    onClick={() => setActiveMediaStep((prev) => prev - 1)}
                    disabled={activeMediaStep === 0}
                    sx={{ border: `1px solid ${LINE}`, borderRadius: "9px" }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                }
              />

              {/* Optional thumbnails */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  overflowX: "auto",
                  marginTop: 15,
                  paddingBottom: 10,
                }}
              >
                {selectedMedia.map((media, index) => {
                  const isVideo =
                    media.url?.includes(".mp4") ||
                    media.url?.includes(".mov") ||
                    media.url?.includes(".webm");

                  return (
                    <div
                      key={index}
                      onClick={() => setActiveMediaStep(index)}
                      style={{
                        minWidth: 80,
                        width: 80,
                        height: 80,
                        border:
                          activeMediaStep === index
                            ? `2px solid ${PRIMARY}`
                            : `1px solid ${LINE}`,
                        borderRadius: 10,
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                    >
                      {isVideo ? (
                        <video
                          src={media.url}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <img
                          src={media.url}
                          alt={`thumb-${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryList;
